import { z } from 'zod';

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const slugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(SLUG_PATTERN, 'Slug must be lowercase kebab-case (a-z, 0-9, and hyphens).');

// Letters NFKD cannot decompose into "ASCII + combining mark". Without these a
// Turkish title like "Kahve Dükkanı" would slug to "kahve-dukkan-" (the dotless
// ı is not a-z, so it becomes a separator) — every non-English customer would
// get a mangled public URL. Keys are lowercase: transliteration runs AFTER
// toLowerCase(). Multi-char expansions follow the usual convention (ß → ss).
const TRANSLITERATE: Record<string, string> = {
  ı: 'i', // Turkish dotless i (U+0131) — "İ" needs no entry: toLowerCase + NFKD already yields "i"
  ß: 'ss',
  æ: 'ae',
  ø: 'o',
  œ: 'oe',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ħ: 'h',
  ŋ: 'ng',
  ĸ: 'k',
};

const TRANSLITERATE_RE = new RegExp(`[${Object.keys(TRANSLITERATE).join('')}]`, 'g');

// Title → URL slug. Non-ASCII letters are transliterated (ü→u, ş→s, ı→i, ß→ss)
// rather than dropped, so "Kahve Dükkanı Müşteri Anketi" becomes
// "kahve-dukkani-musteri-anketi". Anything still unmappable (CJK, emoji) is a
// separator, exactly as before.
//
// Callers: this only ever DERIVES a new slug from a title at write time. Stored
// slugs are read from their row and never recomputed, so changing the mapping
// cannot orphan an existing URL.
export function toSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(TRANSLITERATE_RE, (c) => TRANSLITERATE[c] ?? c)
    // Decompose the rest (ü → u + ¨) and drop the combining marks.
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    // NFKD runs AFTER the first lowercase, and compatibility decompositions
    // can emit uppercase ASCII (No 2116 -> "No", Rs -> "Rs", MHz -> "MHz").
    // Without this second fold the capital becomes a separator and only the
    // tail survives ("Ofis No 3" -> "ofis-o-3").
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    // A trailing hyphen can reappear after the 64-char cut.
    .replace(/-+$/g, '');
}

// Derive a slug that always satisfies slugSchema's 2-char minimum, prefixing
// when the title alone is too short or has nothing transliterable ("日本語" →
// "site", "A" → "site-a"). Without this a caller storing `toSlug(name)`
// directly can persist an EMPTY slug — an unreachable row. Mirrors the shape
// deriveUniqueSurveySlug already uses for surveys.
export function toSlugWithFallback(input: string, prefix: string): string {
  const root = toSlug(input);
  if (root.length >= 2) return root;
  return `${prefix}-${root}`.replace(/-+$/g, '').slice(0, 64);
}

// Reserved slugs for system routes; cannot be assigned to a site.
export const RESERVED_SLUGS = new Set([
  'admin',
  'api',
  'app',
  'auth',
  'billing',
  // The renderer's demo path prefix (2026-08-30): unclaimed cold-outreach
  // demos are served at `web.pageboss.com/d/{slug}` so the ONE platform
  // robots.txt can disallow `/d/` for Google-Extended + Applebot-Extended
  // without naming a single business. A site holding the slug `d` would sit
  // at `/d` and its own pages at `/d/{page}` — the exact demo namespace.
  // (slugSchema's min(2) already rejects it; this is the rule stated where
  // the reservation is read, not an accident of a length check.)
  'd',
  'dashboard',
  'demo',
  'help',
  'login',
  'logout',
  'preview',
  'pricing',
  'privacy',
  'signup',
  'support',
  'terms',
  'webhooks',
  'www',
]);
